import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "../context/TranslationContext";
import { getNewsBySlug, getPublishedNews } from "../api/content";
import CardMedia from "../components/discover/CardMedia";
import Button from "../components/ds/Button";
import Avatar from "../components/ds/Avatar";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import Page from "../components/ds/Page";

/** "3 min read", at the 200 words a minute people read on a screen. */
const readTime = (content, t) => {
  const words = String(content || "").trim().split(/\s+/).filter(Boolean).length;
  return words ? t("resources.minRead", { count: Math.max(1, Math.round(words / 200)) }) : "";
};

const NewsArticlePage = ({ setToast }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [more, setMore] = useState([]);
  const { t } = useTranslation();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  /** Other posts for "More from the blog"; the page reads fine without them. */
  useEffect(() => {
    let cancelled = false;
    getPublishedNews(4)
      .then((res) => !cancelled && res.success && setMore(res.data || []))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getNewsBySlug(decodeURIComponent(slug));
        if (cancelled) return;
        if (res.success && res.data) setArticle(res.data);
        else setArticle(null);
      } catch (e) {
        if (!cancelled) {
          setArticle(null);
          setToast?.(e?.response?.data?.message || t("resources.articleNotFound"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, setToast, t]);

  const back = (
    <Button variant="plain" size="s" icon={<Icon name="arrow-left" size={16} />} className="self-start" onClick={() => navigate("/resources")}>
      {t("resources.backToResources")}
    </Button>
  );

  if (loading) {
    return (
      <Page>
        <p className="text-subheadline text-label-secondary">{t("common.loading")}</p>
      </Page>
    );
  }

  if (!article) {
    return (
      <Page>
        {back}
        <EmptyState icon="book" title={t("resources.articleNotFound")} />
      </Page>
    );
  }

  const others = more.filter((item) => item.slug !== article.slug).slice(0, 3);

  return (
    <Page>
      {back}
      <article className="flex flex-col gap-space-40">
        {article.coverImageUrl && (
          <div className="relative h-[220px] overflow-hidden rounded-radius-xl min-[700px]:h-[400px]">
            <img src={article.coverImageUrl} alt="" className="h-full w-full object-cover" />
            <span className="absolute left-space-16 top-space-16 rounded-full bg-black/45 px-space-12 py-[6px] text-caps-badge uppercase text-white backdrop-blur-md min-[700px]:left-space-24 min-[700px]:top-space-24">
              {t("resources.newsTitle")}
            </span>
          </div>
        )}
        <div className="flex flex-col gap-space-16">
          <h1 className="text-title-1 text-label-primary min-[700px]:text-large-title">{article.title}</h1>
          <div className="flex items-center gap-[10px]">
            <Avatar size="m" name="Mintly Team" initials="MT" />
            <div className="flex flex-col gap-[2px]">
              <p className="text-footnote-semibold text-label-primary">{t("resources.authorTeam")}</p>
              <p className="text-caption-1 text-label-tertiary">
                {[article.publishedAt && new Date(article.publishedAt).toLocaleDateString(), readTime(article.content, t)]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
          </div>
          {article.excerpt && <p className="text-body text-label-secondary">{article.excerpt}</p>}
          <hr className="my-space-8 border-separator-default" />
          <div className="whitespace-pre-wrap text-[17px] leading-[1.6] text-label-secondary">{article.content}</div>
        </div>

        {others.length > 0 && (
          <section className="flex flex-col gap-space-16">
            <h2 className="text-title-2 text-label-primary">{t("resources.moreFromBlog")}</h2>
            <div className="grid grid-cols-1 gap-space-16 min-[700px]:grid-cols-3 min-[700px]:gap-space-24">
              {others.map((item) => (
                <Link
                  key={item._id}
                  to={`/resources/news/${encodeURIComponent(item.slug)}`}
                  className="flex flex-col overflow-hidden rounded-[24px] bg-bg-raised outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
                >
                  <CardMedia src={item.coverImageUrl} placeholderIcon="book" placeholderLabel={t("resources.articleLabel")} className="h-[180px]" />
                  <div className="flex flex-col gap-[6px] p-space-16">
                    <h3 className="line-clamp-2 text-headline text-label-primary">{item.title}</h3>
                    {item.publishedAt && (
                      <p className="text-footnote text-label-tertiary">{new Date(item.publishedAt).toLocaleDateString()}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </Page>
  );
};

export default NewsArticlePage;
